/**
 * Public display identity helpers.
 *
 * LOCKED RULE (PRODUCT_CONTRACT.md):
 * Public name (SWYG X …) = Avatar tag if set, else Display name / Nickname, else legal first name.
 * Never use surname / last name as the public name.
 */

export type DisplayNameSource = {
  avatarName?: string | null;
  nickname?: string | null;
  profile?: { avatarName?: string | null; nickname?: string | null } | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

function clean(value?: string | null): string {
  return typeof value === 'string' ? value.trim() : '';
}

function sameName(a: string, b?: string | null): boolean {
  const other = clean(b);
  return !!other && a.toLowerCase() === other.toLowerCase();
}

/**
 * First non-empty candidate that is not the legal surname.
 * Used at onboarding save and at display time so "Sahu" cannot become SWYG X Sahu.
 */
export function pickPublicName(
  candidates: Array<string | null | undefined>,
  lastName?: string | null
): string {
  for (const candidate of candidates) {
    const value = clean(candidate);
    if (value && !sameName(value, lastName)) return value;
  }
  return '';
}

/** Name shown in "SWYG X …" headers and greetings. */
export function getDisplayName(
  user: DisplayNameSource | null | undefined,
  fallback = 'User'
): string {
  if (!user) return fallback;
  const publicName = pickPublicName(
    [
      user.avatarName,
      user.profile?.avatarName,
      user.nickname,
      user.profile?.nickname,
      user.firstName,
    ],
    user.lastName
  );
  if (publicName) return publicName;
  const emailLocal = clean(user.email).split('@')[0];
  if (emailLocal && !sameName(emailLocal, user.lastName)) return emailLocal;
  return fallback;
}

/** Initials / short tag for the avatar chip — from public name, never last name alone. */
export function getDisplayInitials(
  user: DisplayNameSource | null | undefined,
  fallback = 'SW'
): string {
  const name = getDisplayName(user, '');
  if (!name) return fallback;
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  // Compact avatar tags like RKS / CG show in full on the chip.
  if (/^[A-Za-z]{2,4}$/.test(name)) {
    return name.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || fallback;
}

/**
 * Day counter for Profile.
 * Day 0 = calendar day the user's profile (or account) was created.
 * Company launch date is NOT used — a new joiner is not day 21 of an old org.
 */
export function getProgramDayCount(options: {
  profileCreatedAt?: string | Date | null;
  userCreatedAt?: string | Date | null;
  /** @deprecated Ignored — kept so old call sites do not silently switch to company aging. */
  companyCreatedAt?: string | Date | null;
  maxDays?: number;
}): number {
  const maxDays = options.maxDays ?? 30;
  const raw = options.profileCreatedAt || options.userCreatedAt;
  if (!raw) return 0;
  const created = new Date(raw);
  if (Number.isNaN(created.getTime())) return 0;
  const start = new Date(created);
  start.setHours(0, 0, 0, 0);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const days = Math.floor((todayStart.getTime() - start.getTime()) / 86400000);
  return Math.max(0, Math.min(maxDays, days));
}
